import { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import api from 'services/api';
import { useSubmit } from '../../hooks/useSubmit';
import useSEO from 'hooks/useSEO';

const SUBJECTS = [
  'General Inquiry',
  'Technical Support',
  'Billing & Subscription',
  'Feature Request',
  'Bug Report',
  'Partnership',
  'Other',
];

export default function ContactPage() {
  useSEO({
    title: 'Contact Us',
    description: 'Get in touch with the Safe Dividend Tracker team. We\'re here to help with questions about your dividend portfolio tracker, billing, or feature requests.',
    canonical: '/contact',
  });

  const [form, setForm]     = useState({ name: '', email: '', subject: SUBJECTS[0], message: '' });
  const [errors, setErrors] = useState({});
  const { run: runSend, isLoading: sending } = useSubmit();
  const [sent, setSent]     = useState(false);
  const [serverError, setServerError] = useState('');

  function validate() {
    const e = {};
    if (!form.name.trim())    e.name    = 'Please enter your name.';
    if (!form.email.trim())   e.email   = 'Please enter your email address.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (!form.message.trim()) e.message = 'Please enter your message.';
    return e;
  }

  function handleSubmit(e) {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    runSend(async () => {
      try {
        await api.post('/contact', form);
        setSent(true);
      } catch {
        setServerError('Something went wrong. Please try emailing us directly at support@safedividendtracker.com');
      }
    });
  }

  function handleChange(field, value) {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Navbar ── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '0 48px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo size={36} />
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', fontFamily: 'var(--font-heading)' }}>Safe Dividend Tracker</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {[['/#features','Features'],['/#pricing','Pricing'],['/blog','Blog'],['/#faq','FAQ'],['/contact','Contact'],['/privacy-policy','Privacy']].map(([href, label]) => (
            <Link key={label} to={href} style={{ fontSize: 14, color: label === 'Contact' ? '#2E7D32' : '#64748B', fontWeight: label === 'Contact' ? 700 : 500, textDecoration: 'none', fontFamily: 'var(--font-caption)' }}>{label}</Link>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/login" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'var(--font-caption)' }}>Sign In</Link>
          <Link to="/register" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none', fontFamily: 'var(--font-caption)', boxShadow: '0 2px 8px rgba(46,125,50,0.3)' }}>Get Started Free</Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(to bottom, #f0fdf4, #fff)', padding: '56px 48px 48px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f5e9', border: '1px solid #bbf7d0', color: '#2E7D32', fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 20, marginBottom: 18 }}>
          💬 We'd love to hear from you
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 900, color: '#0F172A', margin: '0 0 14px', letterSpacing: '-0.03em', lineHeight: 1.15 }}>Contact Us</h1>
        <p style={{ fontSize: 16, color: '#64748B', maxWidth: 480, margin: '0 auto' }}>
          Have a question, idea or issue? Send us a message and we'll get back to you within one business day.
        </p>
      </div>

      {/* ── Main content ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 24px 96px', display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 56, alignItems: 'start' }}>

        {/* ── Left: contact info ── */}
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>Get in touch</h2>
          <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, marginBottom: 36 }}>
            We're a small team dedicated to helping dividend investors. Reach out any time — we read every message.
          </p>

          {/* Address */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="MapPin" size={18} color="#2E7D32" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>Office Address</div>
              <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.65 }}>
                8700 Trail Lake Dr, Suite 228<br />
                Memphis, TN 38125
              </div>
              <a
                href="https://maps.google.com/?q=8700+Trail+Lake+Dr+Suite+228+Memphis+TN+38125"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: '#2E7D32', fontWeight: 600, textDecoration: 'none', marginTop: 6, display: 'inline-block' }}
              >
                View on Google Maps →
              </a>
            </div>
          </div>

          {/* Support email */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="Mail" size={18} color="#2E7D32" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>Support</div>
              <a href="mailto:support@safedividendtracker.com" style={{ fontSize: 14, color: '#2E7D32', textDecoration: 'none' }}>support@safedividendtracker.com</a>
            </div>
          </div>

          {/* Privacy email */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 40 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="Shield" size={18} color="#2E7D32" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>Privacy</div>
              <a href="mailto:privacy@safedividendtracker.com" style={{ fontSize: 14, color: '#2E7D32', textDecoration: 'none' }}>privacy@safedividendtracker.com</a>
            </div>
          </div>

          {/* Response time */}
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <Icon name="Clock" size={16} color="#64748B" style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 3 }}>Response Time</div>
              <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>We typically respond within 1 business day, Monday – Friday.</div>
            </div>
          </div>
        </div>

        {/* ── Right: form ── */}
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 16, padding: '40px 40px', boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>

          {sent ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Icon name="CheckCircle" size={32} color="#2E7D32" />
              </div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', marginBottom: 10 }}>Message Sent!</h3>
              <p style={{ fontSize: 15, color: '#64748B', lineHeight: 1.7, maxWidth: 360, margin: '0 auto 28px' }}>
                Thanks for reaching out. We've received your message and will reply within one business day.
              </p>
              <button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: SUBJECTS[0], message: '' }); }}
                style={{ fontSize: 14, fontWeight: 700, color: '#2E7D32', background: '#e8f5e9', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer' }}>
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', marginBottom: 24 }}>Send us a message</h3>

              {serverError && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#DC2626' }}>
                  {serverError}
                </div>
              )}

              {/* Name + Email row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Your Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => handleChange('name', e.target.value)}
                    placeholder="John Smith"
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.name ? '#EF4444' : '#E2E8F0'}`, borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }}
                  />
                  {errors.name && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.name}</p>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email Address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="john@example.com"
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.email ? '#EF4444' : '#E2E8F0'}`, borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }}
                  />
                  {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.email}</p>}
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Subject</label>
                <select
                  value={form.subject}
                  onChange={e => handleChange('subject', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                >
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Message *</label>
                <textarea
                  value={form.message}
                  onChange={e => handleChange('message', e.target.value)}
                  placeholder="Tell us how we can help…"
                  rows={6}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errors.message ? '#EF4444' : '#E2E8F0'}`, borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
                {errors.message && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={sending}
                style={{ width: '100%', padding: '13px', background: sending ? '#94A3B8' : 'linear-gradient(135deg,#2E7D32,#388E3C)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'opacity 0.2s' }}
              >
                {sending ? (
                  <><span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Sending…</>
                ) : (
                  <><Icon name="Send" size={16} color="white" /> Send Message</>
                )}
              </button>

              <p style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', marginTop: 14 }}>
                We respect your privacy. Your information is never shared with third parties.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* ── Footer strip ── */}
      <div style={{ borderTop: '1px solid #E2E8F0', background: '#F8FAFC', padding: '24px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <p style={{ fontSize: 13, color: '#94A3B8' }}>© {new Date().getFullYear()} Safe Dividend Tracker. All rights reserved.</p>
        <div style={{ display: 'flex', gap: 20 }}>
          {[['Privacy Policy','/privacy-policy'],['Terms of Service','/terms'],['Blog','/blog']].map(([l,h]) => (
            <Link key={l} to={h} style={{ fontSize: 13, color: '#64748B', textDecoration: 'none' }}>{l}</Link>
          ))}
        </div>
      </div>

    </div>
  );
}
